import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Block,
  Button,
  Col,
  Row,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";
import { useHistory } from "react-router-dom";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { formatAdminApiError } from "../../../utils/formatAdminApiError";
import {
  buildClinicMapByPartner,
  derivePartnerTcStatus as resolvePartnerTcStatus,
  isClinicListedForTeleconsultationPlatform,
} from "../../../utils/teleconsultationAdminMetrics";

const PARTNER_PAGE_SIZE = 40;
const GOLIVE_PATH = "teleconsultations/practitioner-approvals";

async function fetchAllAdminClinics(signal) {
  const limit = 100;
  let page = 1;
  const all = [];
  let totalPages = 1;
  for (;;) {
    const res = await http.get("/api/v1/partners/medical/clinics", {
      params: { limit, page },
      signal,
    });
    const chunk = res.data?.data || [];
    const meta = res.data?.meta || {};
    totalPages = Number(meta.totalPages) || 1;
    all.push(...chunk);
    if (page >= totalPages || chunk.length === 0) break;
    page += 1;
  }
  return all;
}

const TeleconsultationPendingPartners = () => {
  const history = useHistory();
  const [allClinics, setAllClinics] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [pendingOnlyPartners, setPendingOnlyPartners] = useState([]);
  const [partnerPage, setPartnerPage] = useState(1);
  const [partnerPagination, setPartnerPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [clinicSearch, setClinicSearch] = useState("");
  const [partnerStatusFilter, setPartnerStatusFilter] = useState("all");
  const [onlyPlatformApprovedClinics, setOnlyPlatformApprovedClinics] = useState(true);
  const [reviewPartner, setReviewPartner] = useState(null);

  const openGolive = () => {
    history.push(getPublicUrl(GOLIVE_PATH));
  };

  const fetchData = useCallback(
    (signal) => {
      setLoading(true);
      setError("");
      Promise.all([
        fetchAllAdminClinics(signal),
        http.get("/api/v1/medical-partners", {
          params: { limit: PARTNER_PAGE_SIZE, page: partnerPage, sort: "-createdAt" },
          signal,
        }),
        http.get("/api/v1/medical-partners", {
          params: { tcApprovalStatus: "pending", limit: 300, sort: "-createdAt" },
          signal,
        }),
      ])
        .then(([clinics, partnersRes, pendingRes]) => {
          setAllClinics(Array.isArray(clinics) ? clinics : []);
          const plist = partnersRes.data?.data ?? [];
          setAllPartners(Array.isArray(plist) ? plist : []);
          setPartnerPagination(partnersRes.data?.pagination ?? null);
          const pend = pendingRes.data?.data ?? [];
          setPendingOnlyPartners(Array.isArray(pend) ? pend : []);
        })
        .catch((e) => {
          if (isAbortError(e)) return;
          setError(formatAdminApiError(e, "Failed to load data"));
          setAllClinics([]);
          setAllPartners([]);
          setPendingOnlyPartners([]);
          setPartnerPagination(null);
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [partnerPage]
  );

  useEffect(() => {
    const ac = new AbortController();
    fetchData(ac.signal);
    return () => ac.abort();
  }, [fetchData]);

  useEffect(() => {
    setPartnerPage(1);
  }, [search, clinicSearch, partnerStatusFilter]);

  const tcClinics = useMemo(
    () => (Array.isArray(allClinics) ? allClinics.filter((c) => c?.offersTeleconsultation === true) : []),
    [allClinics]
  );

  const clinicMapByPartner = useMemo(() => buildClinicMapByPartner(tcClinics), [tcClinics]);

  const partnersWithTcStatus = useMemo(() => {
    const list = Array.isArray(allPartners) ? allPartners : [];
    return list.map((p) => ({
      ...p,
      tcStatusResolved: resolvePartnerTcStatus(p, clinicMapByPartner),
      tcClinics: clinicMapByPartner.get(String(p?._id || "")) || [],
    }));
  }, [allPartners, clinicMapByPartner]);

  const pendingPartners = useMemo(() => {
    const list = Array.isArray(pendingOnlyPartners) ? pendingOnlyPartners : [];
    return list.map((p) => ({
      ...p,
      tcStatusResolved: resolvePartnerTcStatus(p, clinicMapByPartner),
      tcClinics: clinicMapByPartner.get(String(p?._id || "")) || [],
    }));
  }, [pendingOnlyPartners, clinicMapByPartner]);

  const filteredPartners = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = partnersWithTcStatus;
    if (partnerStatusFilter !== "all") {
      list = list.filter((p) => p.tcStatusResolved === partnerStatusFilter);
    }
    if (q) {
      list = list.filter((p) => {
        const name = (p.organization_name || "").toLowerCase();
        const email = (p.contact_person_email || "").toLowerCase();
        const country = (p.contact_person_country || "").toLowerCase();
        return name.includes(q) || email.includes(q) || country.includes(q);
      });
    }
    const cq = clinicSearch.trim().toLowerCase();
    if (cq) {
      list = list.filter((p) =>
        (p.tcClinics || []).some((c) => {
          const cn = (c.name || "").toLowerCase();
          const cc = (c.address?.country || "").toLowerCase();
          return cn.includes(cq) || cc.includes(cq);
        })
      );
    }
    return list;
  }, [partnersWithTcStatus, search, partnerStatusFilter, clinicSearch]);

  const clinicRows = useMemo(() => {
    const cq = clinicSearch.trim().toLowerCase();
    let rows = tcClinics.map((c) => {
      const partnerDoc =
        typeof c.partner === "object" && c.partner != null
          ? c.partner
          : { _id: c.partner, organization_name: "—" };
      return { clinic: c, partner: partnerDoc };
    });
    if (cq) {
      rows = rows.filter(
        ({ clinic, partner }) =>
          (clinic.name || "").toLowerCase().includes(cq) ||
          (partner.organization_name || "").toLowerCase().includes(cq) ||
          (clinic.address?.country || "").toLowerCase().includes(cq)
      );
    }
    if (onlyPlatformApprovedClinics) {
      rows = rows.filter(({ clinic }) => isClinicListedForTeleconsultationPlatform(clinic));
    }
    return rows;
  }, [tcClinics, clinicSearch, onlyPlatformApprovedClinics]);

  const platformTcStatusLabel = (clinic) => {
    const s = String(clinic?.tcPlatformReviewStatus || "not_evaluated").toLowerCase();
    if (s === "approved") return "Approved";
    if (s === "rejected") return "Rejected";
    if (s === "pending_review") return "Pending review";
    if (clinic?.teleconsultationPlatformApproved === false) return "Not approved (legacy)";
    return "Not evaluated";
  };

  const platformTcBadgeClass = (clinic) => {
    const s = String(clinic?.tcPlatformReviewStatus || "not_evaluated").toLowerCase();
    if (s === "approved") return "bg-success";
    if (s === "rejected") return "bg-danger";
    if (s === "pending_review") return "bg-warning";
    if (s === "not_evaluated" && clinic?.teleconsultationPlatformApproved !== false) return "bg-info";
    return "bg-secondary";
  };

  return (
    <>
      {reviewPartner ? (
        <>
          <button
            type="button"
            className="modal-backdrop fade show border-0 p-0"
            style={{ zIndex: 1039, cursor: "pointer" }}
            aria-label="Close review panel"
            onClick={() => setReviewPartner(null)}
          />
          <aside
            className="position-fixed bg-white border-start shadow-sm d-flex flex-column"
            style={{
              top: 0,
              right: 0,
              width: "min(440px, 100vw)",
              height: "100vh",
              zIndex: 1040,
            }}
          >
            <div className="border-bottom p-3 d-flex align-items-start justify-content-between">
              <div className="min-w-0">
                <h6 className="title mb-1 text-truncate">{reviewPartner.organization_name || "Partner"}</h6>
                <p className="small text-soft mb-0 text-break">
                  {reviewPartner.contact_person_email || "—"} · {reviewPartner.contact_person_country || "—"}
                </p>
                <p className="small text-soft mb-0">
                  TC status: <code>{reviewPartner.tcStatusResolved}</code> · TC clinics:{" "}
                  {reviewPartner.tcClinics.length}
                </p>
              </div>
              <Button color="light" size="sm" className="flex-shrink-0" onClick={() => setReviewPartner(null)}>
                Close
              </Button>
            </div>
            <div className="flex-grow-1 overflow-auto p-3 small">
              <div className="alert alert-light border mb-3" role="status">
                Catalog listing and partner program decisions are made in Catalogue go-live.
              </div>
              <h6 className="overline-title text-soft mb-2">Teleconsultation clinics</h6>
              {(reviewPartner.tcClinics || []).length === 0 ? (
                <p className="text-soft mb-0">No TC clinic linked to this partner yet.</p>
              ) : (
                <ul className="list-unstyled mb-0">
                  {reviewPartner.tcClinics.map((c) => (
                    <li key={c._id} className="border rounded p-2 mb-2">
                      <strong className="d-block">{c.name || "Clinic"}</strong>
                      <span className="text-soft">
                        {c.address?.country || "—"} · {c.status || "—"} · platform TC:{" "}
                        {platformTcStatusLabel(c)}
                        {c.tcPlatformReviewStatus === "rejected" && c.tcPlatformRejectionReason ? (
                          <span className="d-block text-danger mt-1">{c.tcPlatformRejectionReason}</span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-top p-3 d-flex flex-wrap align-items-center">
              <Button size="sm" color="primary" className="mr-2 mb-2" onClick={openGolive}>
                Catalogue go-live
              </Button>
              <Button
                size="sm"
                color="primary"
                outline
                className="mb-2"
                onClick={() => history.push(getPublicUrl(`medical-partner-details/${reviewPartner._id}`))}
              >
                Open partner page
              </Button>
            </div>
          </aside>
        </>
      ) : null}
      <TeleconsultationPageShell
        headTitle="TC partner status"
        title="Teleconsultation — partner status"
        description={
          <p className="text-soft mb-0">
            Read-only overview of partner TC program status and clinic listing. Approve or reject catalog listing in{" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={openGolive}>
              Catalogue go-live
            </button>
            .
          </p>
        }
        aside={
          <div className="d-flex flex-wrap">
            <Button color="primary" outline className="mr-2" onClick={openGolive}>
              Catalogue go-live
            </Button>
            <Button color="light" onClick={fetchData} disabled={loading}>
              <Icon name="refresh" />
              Refresh
            </Button>
          </div>
        }
      >
        <Block>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <Row className="mb-4">
            <Col md="6" lg="4" className="mb-3">
              <label className="form-label">Search partners</label>
              <input
                type="search"
                className="form-control"
                placeholder="Name, email, country…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md="6" lg="4" className="mb-3">
              <label className="form-label">Search clinics / country</label>
              <input
                type="search"
                className="form-control"
                placeholder="Clinic name, partner name, country…"
                value={clinicSearch}
                onChange={(e) => setClinicSearch(e.target.value)}
              />
            </Col>
            <Col md="6" lg="4">
              <label className="form-label">Partner TC status</label>
              <select
                className="form-control"
                value={partnerStatusFilter}
                onChange={(e) => setPartnerStatusFilter(e.target.value)}
                aria-label="Filter by partner TC status"
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="off">Off</option>
              </select>
            </Col>
            <Col md="12" className="d-flex align-items-center">
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="onlyApprovedTcClinics"
                  checked={onlyPlatformApprovedClinics}
                  onChange={(e) => setOnlyPlatformApprovedClinics(e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="onlyApprovedTcClinics">
                  Clinics table: show only TC clinics listed on the platform (approved / legacy allowed)
                </label>
              </div>
            </Col>
          </Row>

          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="title mb-3">Pending partner requests ({pendingPartners.length})</h6>
              {loading ? (
                <div className="text-center p-3">
                  <div className="spinner-border text-primary" role="status" />
                </div>
              ) : pendingPartners.length === 0 ? (
                <p className="text-soft mb-0">No pending partner-level requests.</p>
              ) : (
                pendingPartners.map((partner) => (
                  <div key={partner._id} className="border rounded p-2 mb-2">
                    <div className="d-flex flex-wrap align-items-center justify-content-between">
                      <div className="mr-2 mb-2">
                        <strong>{partner.organization_name || "Partner"}</strong>
                        <div className="small text-soft">
                          {partner.contact_person_email || "—"} · {partner.contact_person_country || "—"} · TC
                          clinics: {partner.tcClinics.length}
                        </div>
                      </div>
                      <div className="d-flex flex-wrap align-items-center">
                        <Button size="sm" color="light" className="mr-2 mb-2" onClick={() => setReviewPartner(partner)}>
                          View
                        </Button>
                        <Button size="sm" color="primary" className="mr-2 mb-2" onClick={openGolive}>
                          Catalogue go-live
                        </Button>
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          className="mb-2"
                          onClick={() => history.push(getPublicUrl(`medical-partner-details/${partner._id}`))}
                        >
                          Open partner
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="title mb-3">Clinics (TC) — platform listing status</h6>
              <p className="text-soft small mb-3">
                Read-only clinic listing status. Decisions are made in Catalogue go-live.
              </p>
              {clinicRows.length === 0 ? (
                <p className="text-soft mb-0">No clinics match the current filters.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Clinic</th>
                        <th>Partner</th>
                        <th>Clinic status</th>
                        <th>Platform TC</th>
                        <th>Note</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clinicRows.map(({ clinic, partner }) => (
                        <tr key={clinic._id}>
                          <td>
                            <strong>{clinic.name || "—"}</strong>
                            <div className="small text-soft">{clinic.address?.country || "—"}</div>
                          </td>
                          <td>{partner.organization_name || "—"}</td>
                          <td>{clinic.status || "—"}</td>
                          <td>
                            <span className={`badge badge-dim ${platformTcBadgeClass(clinic)}`}>
                              {platformTcStatusLabel(clinic)}
                            </span>
                            <div className="small text-soft mt-1">
                              Listed: {isClinicListedForTeleconsultationPlatform(clinic) ? "yes" : "no"}
                            </div>
                          </td>
                          <td className="small text-break" style={{ maxWidth: 220 }}>
                            {clinic.tcPlatformReviewStatus === "rejected" && clinic.tcPlatformRejectionReason
                              ? clinic.tcPlatformRejectionReason
                              : "—"}
                          </td>
                          <td className="text-end text-nowrap">
                            <Button size="sm" color="primary" outline onClick={openGolive}>
                              Catalogue go-live
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card card-bordered">
            <div className="card-inner">
              <h6 className="title mb-3">All partners</h6>
              <p className="text-soft small mb-3">
                Partner list is loaded from the server in pages of {PARTNER_PAGE_SIZE}. Filters below apply to the
                current page only.
                {partnerPagination?.total != null ? (
                  <>
                    {" "}
                    Total partners matching the API query: <strong>{partnerPagination.total}</strong>.
                  </>
                ) : null}
              </p>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Partner</th>
                      <th>TC status</th>
                      <th>TC clinics</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPartners.map((partner) => (
                      <tr key={partner._id}>
                        <td>
                          <div>{partner.organization_name || "—"}</div>
                          <div className="small text-soft">{partner.contact_person_email || "—"}</div>
                        </td>
                        <td>
                          <code className="small">{partner.tcStatusResolved}</code>
                        </td>
                        <td>{partner.tcClinics.length}</td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            color="light"
                            className="me-1"
                            onClick={() => setReviewPartner(partner)}
                          >
                            View
                          </Button>
                          <Button size="sm" color="primary" outline className="me-1" onClick={openGolive}>
                            Catalogue go-live
                          </Button>
                          <Button
                            size="sm"
                            color="light"
                            onClick={() => history.push(getPublicUrl(`medical-partner-details/${partner._id}`))}
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {partnerPagination && partnerPagination.pages > 1 ? (
                <div className="mt-3">
                  <PaginationComponent
                    itemPerPage={partnerPagination.limit || PARTNER_PAGE_SIZE}
                    totalItems={partnerPagination.total || 0}
                    paginate={setPartnerPage}
                    currentPage={partnerPage}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </Block>
      </TeleconsultationPageShell>
    </>
  );
};

export default TeleconsultationPendingPartners;
